import type { EntryKey } from "@pinia/colada";
import type { VerifyMfaBody } from "@/api/auth.api";
import type { Organization, SipConfigurationInsert } from "@/schemas/organization.schema";
import type { UserSettings } from "@/schemas/settings.schema";
import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import rest from "@/api/rest";
import { api } from "@/services/api";

// ============= Query Keys =============

export const SETTINGS_QUERY_KEYS = {
  root: ["settings"] as const,
  user: () => [...SETTINGS_QUERY_KEYS.root, "user"] as const,
  organization: () => [...SETTINGS_QUERY_KEYS.root, "organization"] as const,
};

// ============= Read Queries =============

/**
 * Query for user settings
 * Returns user profile data (name, email, language, MFA status)
 *
 * @example
 * const userQuery = useUserSettingsQuery();
 * const language = computed(() => userQuery.data.value?.language);
 */
export function useUserSettingsQuery() {
  return useQuery({
    key: SETTINGS_QUERY_KEYS.user() as unknown as EntryKey,
    query: async () => {
      const { data } = await api.get<UserSettings>("/settings/user");
      return data;
    },
    staleTime: 1000 * 30, // 30 seconds - settings don't change often during session
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Query for organization settings
 * Returns organization configuration (name, currency, low credit alerts, SIP config)
 *
 * @example
 * const orgQuery = useOrganizationSettingsQuery();
 * const orgName = computed(() => orgQuery.data.value?.name);
 */
export function useOrganizationSettingsQuery() {
  return useQuery({
    key: SETTINGS_QUERY_KEYS.organization() as unknown as EntryKey,
    query: async () => {
      const { data } = await api.get<Organization>("/settings/organization");
      return data;
    },
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============= Mutations =============

/**
 * Mutation to update user settings
 * Auto-invalidates user settings cache on success
 *
 * @example
 * const { mutate, isPending } = useUpdateUserSettings();
 * await mutate({ full_name: 'New Name', language: 'en' });
 */
export function useUpdateUserSettings() {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: async (user: Partial<UserSettings>) => {
      await rest.settings.updateUserSettings(user);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: SETTINGS_QUERY_KEYS.user() as unknown as EntryKey });
    },
  });
}

/**
 * Mutation to update organization settings
 * Auto-invalidates organization settings cache on success
 *
 * @example
 * const { mutate } = useUpdateOrganizationSettings();
 * await mutate({ name: 'New Org Name', settings: { low_credit_alert: 100 } });
 */
export function useUpdateOrganizationSettings() {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: async (organization: Partial<Pick<Organization, "name" | "settings">>) => {
      await rest.settings.updateOrganizationSettings(organization);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({
        key: SETTINGS_QUERY_KEYS.organization() as unknown as EntryKey,
      });
    },
  });
}

/**
 * Mutation to update SIP configuration
 * Auto-invalidates organization settings cache on success
 *
 * @example
 * const { mutate } = useUpdateSipConfiguration();
 * await mutate({ inbound_trunk: { ... } });
 */
export function useUpdateSipConfiguration() {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: async (config: SipConfigurationInsert) => {
      await rest.settings.updateSipSettings(config);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({
        key: SETTINGS_QUERY_KEYS.organization() as unknown as EntryKey,
      });
    },
  });
}

// ============= MFA Mutations =============

/**
 * Mutation to generate MFA QR code
 * Returns QR code URL for authenticator app setup
 *
 * @example
 * const { mutate } = useGenerateMfa();
 * const result = await mutate();
 * // result.qr_code
 */
export function useGenerateMfa() {
  return useMutation({
    mutation: async () => {
      return await rest.settings.generateMfa();
    },
  });
}

/**
 * Mutation to verify MFA code
 * Returns recovery code to be shown to user
 *
 * @example
 * const { mutate } = useVerifyMfa();
 * const result = await mutate('123456');
 * // result.recovery_code
 */
export function useVerifyMfa() {
  return useMutation({
    mutation: async (code: string) => {
      return await rest.settings.verifyMfa(code);
    },
  });
}

/**
 * Mutation to enable MFA
 * Auto-invalidates user settings cache on success (updates MFA status)
 *
 * @example
 * const { mutate } = useEnableMfa();
 * await mutate(recovery_code);
 */
export function useEnableMfa() {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: async (recovery_code: string) => {
      await rest.settings.enableMfa(recovery_code);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: SETTINGS_QUERY_KEYS.user() as unknown as EntryKey });
    },
  });
}

/**
 * Mutation to disable MFA
 * Auto-invalidates user settings cache on success (updates MFA status)
 *
 * @example
 * const { mutate } = useDisableMfa();
 * await mutate({ type: 'authenticator', code: '123456' });
 */
export function useDisableMfa() {
  const queryCache = useQueryCache();

  return useMutation({
    mutation: async (body: VerifyMfaBody) => {
      await rest.settings.disableMfa(body);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: SETTINGS_QUERY_KEYS.user() as unknown as EntryKey });
    },
  });
}

// ============= Other Mutations =============

/**
 * Mutation to logout from all devices
 * No cache invalidation needed (user will be logged out)
 *
 * @example
 * const { mutate } = useLogoutAll();
 * await mutate();
 */
export function useLogoutAll() {
  return useMutation({
    mutation: async () => {
      await rest.settings.logoutAll();
    },
  });
}

// ============= Cache Invalidation =============

export function useInvalidateSettings() {
  const queryCache = useQueryCache();

  return {
    /**
     * Invalidate all settings queries
     */
    all: () =>
      queryCache.invalidateQueries({ key: SETTINGS_QUERY_KEYS.root as unknown as EntryKey }),

    /**
     * Invalidate only user settings
     */
    user: () =>
      queryCache.invalidateQueries({ key: SETTINGS_QUERY_KEYS.user() as unknown as EntryKey }),

    /**
     * Invalidate only organization settings
     */
    organization: () =>
      queryCache.invalidateQueries({
        key: SETTINGS_QUERY_KEYS.organization() as unknown as EntryKey,
      }),
  };
}
